import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Meta, Display, Chip, Card, PageTitle, Icons } from '../components/Atoms';
import Layout, { TopBar } from '../components/Layout';
import { LoadingState, EmptyState } from '../components/states';
import { useApi } from '../lib/api';
import axios from '../../config/axios';
import CommunityTabs from '../components/CommunityTabs';

const REACTION_ICONS = { like: '👍', sprout: '🌱', sunflower: '🌻', carrot: '🥕' };

function ReactionPicker({ targetType, targetId, reactions }) {
  // PQ1: gdy `reactions` przyszło z feedu (props) — NIE fetchujemy per element (koniec N+1);
  // bez propa (np. wywołania spoza feedu) fallback do useApi. Po akcji update z odpowiedzi POST.
  const { data: fetched, refetch } = useApi(reactions ? null : `/api/interactions/${targetType}/${targetId}/reactions`);
  const [override, setOverride] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const data = override || reactions || fetched;
  if (!data) return null;
  const myReaction = data.myReaction;

  const pickReaction = async (type) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`/api/interactions/${targetType}/${targetId}/reaction`, { type });
      setShowPicker(false);
      if (reactions) setOverride({ counts: res.data?.counts || [], myReaction: type });
      else refetch();
    } catch (e) { console.error(e); }
    setBusy(false);
  };

  const removeReaction = async () => {
    if (busy || !myReaction) return;
    setBusy(true);
    try {
      const res = await axios.delete(`/api/interactions/${targetType}/${targetId}/reaction`);
      if (reactions) setOverride({ counts: res.data?.counts || [], myReaction: null });
      else refetch();
    } catch (e) { console.error(e); }
    setBusy(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setShowPicker(!showPicker)}
        disabled={busy}
        aria-label={myReaction ? 'Zmień reakcję' : 'Dodaj reakcję'}
        style={{
          padding: '4px 10px', borderRadius: 6,
          background: myReaction ? 'var(--t-sage-bg)' : 'transparent',
          border: `1px solid ${myReaction ? 'var(--t-sage)' : 'var(--t-line-2)'}`,
          color: myReaction ? 'var(--t-sage)' : 'var(--t-text-dim)',
          fontSize: 16, cursor: busy ? 'wait' : 'pointer', fontFamily: 'var(--t-sans)',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
        {myReaction ? REACTION_ICONS[myReaction] : <Icons.Sprout size={16} />} {Object.keys(data.counts || {}).length > 0 ? Object.values(data.counts).reduce((a,b) => a + b.cnt, 0) : 0}
      </button>
      {showPicker && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, zIndex: 100,
          display: 'flex', gap: 4, padding: '6px 8px', borderRadius: 8,
          background: 'var(--t-bg-elev)', border: '1px solid var(--t-line)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {Object.entries(REACTION_ICONS).map(([type, icon]) => (
            <button
              key={type}
              type="button"
              onClick={() => pickReaction(type)}
              disabled={busy}
              aria-label={`Reakcja ${type}`}
              style={{
                background: 'none', border: 'none', fontSize: 18, cursor: 'pointer',
                opacity: myReaction === type ? 1 : 0.7, transform: myReaction === type ? 'scale(1.2)' : 'scale(1)',
              }}>{icon}</button>
          ))}
          {myReaction && (
            <button
              type="button"
              onClick={removeReaction}
              disabled={busy}
              aria-label="Usuń reakcję"
              style={{
                background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', color: 'var(--t-rust)',
              }}>✕</button>
          )}
        </div>
      )}
    </div>
  );
}

function LikeButton({ targetType, targetId, reactions }) {
  // Legacy wrapper dla S3
  return <ReactionPicker targetType={targetType} targetId={targetId} reactions={reactions} />;
}

function CommentBox({ targetType, targetId, onUpdate }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!text.trim() || text.length > 300) return;
    setSaving(true); setErr('');
    try {
      await axios.post(`/api/interactions/${targetType}/${targetId}/comment`, { body: text.trim() });
      setText('');
      onUpdate?.();
    } catch (e) { setErr(e.response?.data?.error || 'Nie udało się dodać komentarza. Spróbuj ponownie.'); }
    setSaving(false);
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="text" aria-label="Komentarz" value={text} onChange={e => setText(e.target.value)}
          maxLength={300} placeholder="Komentarz… (max 300 znaków)"
          onKeyDown={e => e.key === 'Enter' && submit()}
          style={{
            flex: 1, padding: '6px 10px', borderRadius: 6,
            background: 'var(--t-bg)', border: '1px solid var(--t-line-2)',
            color: 'var(--t-text)', fontSize: 12, fontFamily: 'var(--t-sans)',
          }} />
        <button type="button" onClick={submit} disabled={saving || !text.trim() || text.length > 300} style={{
          padding: '6px 12px', borderRadius: 6,
          background: 'var(--t-sage)', border: 'none',
          color: 'var(--t-bg)', fontSize: 12, cursor: 'pointer',
          opacity: saving || !text.trim() ? 0.5 : 1,
        }}>{saving ? '…' : 'Wyślij'}</button>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 2, fontSize: 10, color: 'var(--t-text-mute)' }}>
        <span>{text.length}/300</span>
        {err && <span style={{ color: 'var(--t-rust)' }}>{err}</span>}
      </div>
    </div>
  );
}

function ListingCard({ item }) {
  const typeLabel = { sell: 'Sprzedam', exchange: 'Wymienię', give: 'Oddam' };
  const typeTone = { sell: 'amber', exchange: 'dim', give: 'sage' };
  return (
    <Card style={{ marginBottom: 10, borderLeft: '3px solid var(--t-amber)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Chip tone={typeTone[item.listingType] || 'dim'}>{typeLabel[item.listingType] || item.listingType}</Chip>
        <span style={{ fontSize: 11, color: 'var(--t-text-dim)' }}>{item.displayName}</span>
      </div>
      <div style={{ fontSize: 14, color: 'var(--t-text)', marginBottom: 6 }}>
        {item.title || item.plantName}
      </div>
      <div style={{ fontSize: 11, color: 'var(--t-text-dim)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {item.city && <Meta><Icons.Plot size={10} style={{ marginRight: 4 }} />{item.city}</Meta>}
        {item.quantity && <Meta>{item.quantity}</Meta>}
        {item.price != null && <Meta color="amber">{item.price} zł</Meta>}
      </div>
    </Card>
  );
}

function FeedItem({ item }) {
  // PQ1: komentarze i reakcje przychodzą JUŻ z feedu (props) — koniec N+1. Fetch tylko gdy user
  // doda komentarz (reloadComments), nie na każdym renderze.
  const [comments, setComments] = useState(item.comments || []);
  const reloadComments = async () => {
    const tid = item.id || item.photoId;
    try {
      const res = await axios.get(`/api/interactions/${item.type}/${tid}`);
      setComments(res.data?.comments || []);
    } catch (e) { console.error(e); }
  };
  const totalComments = Math.max(comments.length, item.commentCount || 0);

  if (item.type === 'harvest') {
    return (
      <Card style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: 'var(--t-text)', marginBottom: 4 }}>
          <strong>{item.displayName}</strong> zebrał {item.amount} {item.unit} <strong>{item.plantName}</strong>
        </div>
        <div style={{ fontSize: 11, color: 'var(--t-text-dim)', fontFamily: 'var(--t-mono)' }}>
          {new Date(item.date).toLocaleDateString('pl-PL')}
        </div>
        {item.id && <LikeButton targetType="harvest" targetId={item.id} reactions={item.reactions} />}
      </Card>
    );
  }
  if (item.type === 'photo') {
    const phId = item.id || item.photoId;
    return (
      <Card style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: 'var(--t-text)', marginBottom: 4 }}>
          <strong>{item.displayName}</strong> dodał{item.caption ? `: ${item.caption}` : ' zdjęcie'}
        </div>
        <div style={{ fontSize: 11, color: 'var(--t-text-dim)', fontFamily: 'var(--t-mono)' }}>
          {new Date(item.date).toLocaleDateString('pl-PL')}
        </div>
        {phId && (
          <>
            <div style={{ marginTop: 8 }}>
              <LikeButton targetType="photo" targetId={phId} reactions={item.reactions} />
            </div>
            {comments.slice(0, 3).map(c => (
              <div key={c.id} style={{
                marginTop: 6, padding: '6px 10px', borderRadius: 6,
                background: 'var(--t-bg)', fontSize: 12, color: 'var(--t-text-dim)',
              }}>
                <strong>{c.public_display_name || c.public_username}:</strong> {c.body}
              </div>
            ))}
            {totalComments > 3 && (
              <div style={{ fontSize: 11, color: 'var(--t-text-mute)', marginTop: 4 }}>
                +{totalComments - 3} więcej
              </div>
            )}
            <CommentBox targetType="photo" targetId={phId} onUpdate={reloadComments} />
          </>
        )}
      </Card>
    );
  }
  if (item.type === 'listing') {
    return <ListingCard item={item} />;
  }
  if (item.type === 'help') {
    return <HelpCard item={item} />;
  }
  if (item.type === 'milestone') {
    return <MilestoneCard item={item} />;
  }
  return null;
}

function MilestoneCard({ item }) {
  const { id, displayName, milestoneType } = item;
  const isStreak = milestoneType.includes('7') || milestoneType.includes('30');
  return (
    <Card style={{ marginBottom: 10, borderLeft: '3px solid var(--t-sage)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Meta color="sage">
          {isStreak ? <Icons.Flame size={12} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} /> : <Icons.Bloom size={12} style={{ marginRight: 4, verticalAlign: 'text-bottom' }} />}
          Kamień milowy
        </Meta>
        <span style={{ fontSize: 10, color: 'var(--t-text-mute)' }}>#{id}</span>
      </div>
      <div style={{ fontSize: 14, color: 'var(--t-text)', marginBottom: 8 }}>
        {displayName} — {milestoneType}
      </div>
      <ReactionPicker targetType="milestone" targetId={id} reactions={item.reactions} />
    </Card>
  );
}

function HelpCard({ item }) {
  const { id, displayName, photo_path, question, plant_name, created_at, status: initialStatus } = item;
  const [status, setStatus] = useState(initialStatus || 'open');
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const loadComments = async () => {
    if (loadingComments) return;
    setLoadingComments(true);
    try {
      const res = await axios.get(`/api/interactions/help/${id}`);
      setComments((res.data?.comments || []).map(c => ({
        ...c,
        username: c.public_display_name || c.public_username,
      })));
      setShowComments(true);
    } catch (e) { console.error(e); }
    setLoadingComments(false);
  };

  const resolveQuestion = async () => {
    try {
      await axios.put(`/api/social/help/${id}/resolve`);
      setStatus('solved');
    } catch (e) { console.error(e); }
  };

  return (
    <Card style={{ marginBottom: 10, borderLeft: '3px solid var(--t-amber)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Meta color="amber"><Icons.Sprout size={12} style={{ marginRight: 4 }} />Pytanie</Meta>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {status === 'solved' && <Chip tone="sage">Rozwiązane</Chip>}
          <span style={{ fontSize: 10, color: 'var(--t-text-mute)' }}>{created_at?.slice(0,10)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {photo_path && (
          <img src={photo_path.startsWith('/') ? photo_path : `/${photo_path}`} alt="" style={{
            width: 60, height: 60, borderRadius: 6, objectFit: 'cover', flexShrink: 0,
            border: '1px solid var(--t-line)',
          }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, color: 'var(--t-text)', marginBottom: 4 }}>
            {displayName} {plant_name && <span style={{ color: 'var(--t-text-dim)' }}>- {plant_name}</span>}
          </div>
          <div style={{ fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.5 }}>{question}</div>
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={loadComments} style={{
          padding: '4px 10px', borderRadius: 6,
          background: 'transparent', border: '1px solid var(--t-line)',
          color: 'var(--t-text-dim)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--t-sans)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <Icons.Journal size={12} /> Odpowiedzi ({comments.length})
        </button>
        {status !== 'solved' && (
          <button type="button" onClick={resolveQuestion} style={{
            padding: '4px 10px', borderRadius: 6,
            background: 'var(--t-sage)', border: '1px solid var(--t-sage)',
            color: 'var(--t-bg)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--t-sans)',
          }}>✓ Rozwiązane</button>
        )}
      </div>
      {showComments && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--t-line)' }}>
          {loadingComments ? <LoadingState /> : comments.length === 0 ? (
            <EmptyState title="Brak odpowiedzi" description="Bądź pierwszy!" />
          ) : (
            <div style={{ marginBottom: 10 }}>
              {comments.map(c => (
                <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--t-line)', fontSize: 13, color: 'var(--t-text)' }}>
                  <span style={{ fontWeight: 500 }}>{c.username}:</span> {c.body}
                  <span style={{ fontSize: 10, color: 'var(--t-text-mute)', marginLeft: 8 }}>{c.created_at?.slice(0,16).replace('T', ' ')}</span>
                </div>
              ))}
            </div>
          )}
          {status !== 'solved' && <CommentBox targetType="help" targetId={id} onUpdate={loadComments} />}
        </div>
      )}
    </Card>
  );
}


export default function Feed() {
  const { data, loading, error } = useApi('/api/social/feed');
  const items = Array.isArray(data) ? data : [];

  return (
    <Layout topBar={<TopBar left={<Meta>Społeczność</Meta>} />}>
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 32px 60px' }}>
        <PageTitle
          eyebrow="Społeczność"
          title="Aktywność znajomych"
          sub="Zbiory i zdjęcia Twoich znajomych z ostatnich 30 dni."
        />

        <CommunityTabs active="feed" />

        <div className="t-stagger" style={{ display: 'flex', flexDirection: 'column' }}>
          {loading ? <LoadingState /> :
           error ? <EmptyState title="Nie udało się pobrać" description="Spróbuj ponownie." /> :
           items.length === 0 ? (
             <EmptyState title="Brak aktywności" description="Dodaj znajomych — ich zbiory i zdjęcia pojawią się tutaj." />
           ) : items.map((item, i) => <FeedItem key={i} item={item} />)}
        </div>
      </div>
    </Layout>
  );
}
